import { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { CheckCircle, Loader2, XCircle } from "lucide-react";
import { Button } from "../components/ui/button";
import { useAuth } from "../context/AuthContext";

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function MembershipSuccessPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { token, refreshUser } = useAuth();
  const [status, setStatus] = useState("loading"); // loading, success, error
  const [paymentInfo, setPaymentInfo] = useState(null);

  const sessionId = searchParams.get("session_id");

  useEffect(() => {
    if (!sessionId || !token) return;

    let attempts = 0;
    const maxAttempts = 5;
    const pollInterval = 2000;

    const pollStatus = async () => {
      try {
        const response = await fetch(
          `${API_URL}/api/payments/checkout-status/${sessionId}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        const data = await response.json();

        if (data.payment_status === "paid") {
          setPaymentInfo(data);
          setStatus("success");
          if (refreshUser) await refreshUser();
          return;
        }
        if (data.status === "expired") {
          setStatus("error");
          return;
        }

        attempts++;
        if (attempts < maxAttempts) {
          setTimeout(pollStatus, pollInterval);
        } else {
          setStatus("error");
        }
      } catch {
        attempts++;
        if (attempts < maxAttempts) {
          setTimeout(pollStatus, pollInterval);
        } else {
          setStatus("error");
        }
      }
    };

    pollStatus();
  }, [sessionId, token, refreshUser]);

  return (
    <div className="min-h-screen bg-[#FAFAF9] pt-20 flex items-start justify-center" data-testid="membership-success-page">
      <div className="max-w-md w-full mx-4 mt-12">
        {status === "loading" && (
          <div className="bg-white rounded-2xl shadow-sm border border-stone-100 p-10 text-center">
            <Loader2 className="w-12 h-12 text-burgundy-500 animate-spin mx-auto mb-4" />
            <h2 className="text-lg font-bold text-stone-800 mb-2">결제 확인 중...</h2>
            <p className="text-stone-500 text-sm">잠시만 기다려주세요.</p>
          </div>
        )}

        {status === "success" && (
          <div className="bg-white rounded-2xl shadow-sm border border-stone-100 p-10 text-center">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-10 h-10 text-green-500" />
            </div>
            <h2 className="text-xl font-bold text-stone-800 mb-2">결제가 완료되었습니다!</h2>
            <p className="text-stone-500 text-sm mb-6">
              멤버십이 활성화되었습니다. 새로운 서비스를 이용해보세요.
            </p>
            {paymentInfo && (
              <div className="bg-stone-50 rounded-xl p-4 mb-6 text-left">
                <p className="text-xs text-stone-400 mb-2">결제 정보</p>
                <p className="text-sm text-stone-700">
                  멤버십: <span className="font-semibold">{paymentInfo.metadata?.membership_type?.toUpperCase()}</span>
                </p>
                <p className="text-sm text-stone-700">
                  결제 금액: <span className="font-semibold">USD ${(paymentInfo.amount_total / 100).toFixed(2)}</span>
                </p>
              </div>
            )}
            <Button
              onClick={() => navigate("/profile")}
              className="w-full bg-burgundy-500 hover:bg-burgundy-600 text-white rounded-xl py-3"
              data-testid="go-to-profile-btn"
            >
              내 프로필로 이동
            </Button>
          </div>
        )}

        {status === "error" && (
          <div className="bg-white rounded-2xl shadow-sm border border-stone-100 p-10 text-center">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <XCircle className="w-10 h-10 text-red-500" />
            </div>
            <h2 className="text-xl font-bold text-stone-800 mb-2">결제 확인에 실패했습니다</h2>
            <p className="text-stone-500 text-sm mb-6">
              결제가 완료되지 않았거나 오류가 발생했습니다.
            </p>
            <Button
              onClick={() => navigate("/membership")}
              className="w-full bg-stone-100 hover:bg-stone-200 text-stone-700 rounded-xl py-3"
              data-testid="go-back-btn"
            >
              멤버십 페이지로 돌아가기
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
